import { useEffect, useMemo, type CSSProperties } from 'react'
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'
import { Marked } from 'marked'
import { logger } from '../lib/logger'

const STYLE_ELEMENT_ID = 'markdown-text-styles'

const SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'code',
    'pre',
    'ul',
    'ol',
    'li',
    'blockquote',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'a',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'span',
    'div',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
}

const marked = new Marked({
  gfm: true,
  breaks: true,
})

marked.use({
  renderer: {
    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens)
      const safeHref = href ?? ''
      const titleAttr = title ? ` title="${title}"` : ''
      return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
    },
  },
})

// Injected once, so colours come from the --app-* variables on <html> and
// follow the theme without re-injecting.
const SCOPED_CSS = `
.markdown-text {
  font-size: inherit;
  line-height: 1.55;
  word-break: break-word;
}
.markdown-text > *:first-child { margin-top: 0; }
.markdown-text > *:last-child { margin-bottom: 0; }
.markdown-text p {
  margin: 0 0 0.6em 0;
}
.markdown-text h3,
.markdown-text h4,
.markdown-text h5,
.markdown-text h6 {
  margin: 0.8em 0 0.3em 0;
  font-weight: 600;
  line-height: 1.3;
}
.markdown-text h3 { font-size: 1.05em; }
.markdown-text h4 { font-size: 1em; }
.markdown-text h5,
.markdown-text h6 { font-size: 0.95em; }
.markdown-text strong { font-weight: 600; }
.markdown-text ul,
.markdown-text ol {
  margin: 0 0 0.6em 0;
  padding-left: 1.4em;
}
.markdown-text li {
  margin: 0.15em 0;
}
.markdown-text li > p { margin: 0 0 0.3em 0; }
.markdown-text code {
  background: var(--app-fill);
  padding: 0.05em 0.35em;
  border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
  word-break: break-word;
}
.markdown-text pre {
  background: var(--app-fill);
  padding: 0.6em 0.8em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0 0 0.6em 0;
}
.markdown-text pre code {
  background: transparent;
  padding: 0;
  border-radius: 0;
}
.markdown-text blockquote {
  margin: 0 0 0.6em 0;
  padding: 0.2em 0.8em;
  border-left: 3px solid var(--app-border);
  color: color-mix(in srgb, var(--app-text) 89%, transparent);
  background: var(--app-fill-alter);
}
.markdown-text blockquote > *:last-child { margin-bottom: 0; }
.markdown-text hr {
  border: 0;
  border-top: 1px solid var(--app-border);
  margin: 0.8em 0;
}
.markdown-text a {
  color: var(--app-primary);
  text-decoration: underline;
}
.markdown-text .markdown-text-table-wrap {
  overflow-x: auto;
  margin: 0 0 0.6em 0;
}
.markdown-text table {
  border-collapse: collapse;
  width: auto;
  font-size: 0.95em;
}
.markdown-text th,
.markdown-text td {
  border: 1px solid var(--app-border);
  padding: 0.3em 0.6em;
  text-align: left;
  vertical-align: top;
}
.markdown-text th {
  background: var(--app-fill);
  font-weight: 600;
}
`

function ensureStylesInjected(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ELEMENT_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ELEMENT_ID
  style.textContent = SCOPED_CSS
  document.head.appendChild(style)
}

function wrapTables(html: string): string {
  return html.replace(
    /<table(\s[^>]*)?>([\s\S]*?)<\/table>/g,
    (_match, attrs: string | undefined, inner: string) =>
      `<div class="markdown-text-table-wrap"><table${attrs ?? ''}>${inner}</table></div>`
  )
}

interface MarkdownTextProps {
  value: string | null | undefined
  maxHeight?: number
  className?: string
  style?: CSSProperties
}

export function MarkdownText({
  value,
  maxHeight,
  className,
  style,
}: MarkdownTextProps) {
  useEffect(() => {
    ensureStylesInjected()
  }, [])

  const html = useMemo(() => {
    if (!value) return ''
    try {
      const parsed = marked.parse(value, { async: false })
      const wrapped = wrapTables(typeof parsed === 'string' ? parsed : '')
      return DOMPurify.sanitize(wrapped, SANITIZE_CONFIG)
    } catch (error) {
      logger.warn('Failed to render markdown', { error: String(error) })
      return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    }
  }, [value])

  if (!value) return null

  const composedStyle: CSSProperties = {
    ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}),
    ...style,
  }

  return (
    <div
      className={['markdown-text', className].filter(Boolean).join(' ')}
      style={composedStyle}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
